import { Input } from "antd";
import classNames from "classnames";
import { isNil } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiSearch } from "react-icons/bi";
import { NAME_SPACES } from "shared/locales/constants";
import { fieldOptions, fieldPhrase, readFieldValue } from "../../data-fields";
import { valueText } from "./zones";

const FieldOption = ({ field, label, selected, proposal, payload, onPick }) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const sample = valueText(readFieldValue(field, payload), {
    kind: field.kind,
    t,
  });
  return (
    <button
      type="button"
      className={classNames("dynamic-documents-editor__picker-option", {
        "dynamic-documents-editor__picker-option--selected": selected,
      })}
      onClick={() => onPick(field)}
    >
      <span className="dynamic-documents-editor__picker-label">{label}</span>
      {!isNil(proposal) && (
        <span
          className="dynamic-documents-editor__lucie-tag"
          title={proposal.reason ?? undefined}
        >
          {t("FIELD_PANEL.PROPOSED_BY_LUCIE")}
        </span>
      )}
      {!isNil(sample) && (
        <span className="dynamic-documents-editor__picker-sample">
          {sample}
        </span>
      )}
    </button>
  );
};

const FieldPicker = ({
  selected,
  proposal,
  payload,
  kinds,
  plainOnly = false,
  onPick,
}) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const [search, setSearch] = useState("");
  const proposedPath = proposal?.field?.path;

  const groups = useMemo(
    () =>
      fieldOptions({ search, kinds })
        .map((group) => ({
          ...group,
          options: group.options.filter(
            ({ field }) =>
              field.path !== proposedPath &&
              (!plainOnly || isNil(field.source)),
          ),
        }))
        .filter(({ options }) => options.length > 0),
    [search, kinds, plainOnly, proposedPath],
  );

  const showProposal =
    !isNil(proposal) && (!plainOnly || isNil(proposal.field.source));

  return (
    <div className="dynamic-documents-editor__picker">
      <Input
        allowClear
        size="small"
        prefix={<BiSearch />}
        placeholder={t("FIELD_PANEL.SEARCH")}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <div className="dynamic-documents-editor__picker-list">
        {showProposal && (
          <FieldOption
            field={proposal.field}
            label={fieldPhrase(proposal.field)}
            selected={selected === proposal.field.path}
            proposal={proposal}
            payload={payload}
            onPick={onPick}
          />
        )}
        {groups.length === 0 && !showProposal && (
          <p className="dynamic-documents-editor__muted">
            {t("FIELD_PANEL.NO_FIELD")}
          </p>
        )}
        {groups.map((group) => (
          <div
            key={group.key}
            className="dynamic-documents-editor__picker-group"
          >
            <div className="dynamic-documents-editor__picker-group-title">
              {group.label}
            </div>
            {group.options.map(({ value, label, field }) => (
              <FieldOption
                key={value}
                field={field}
                label={label}
                selected={selected === value}
                payload={payload}
                onPick={onPick}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FieldPicker;
